import * as React from 'react';

// Credit for the pagination component code:
// https://www.freecodecamp.org/news/build-a-custom-pagination-component-in-react/

const range = (start: number, end: number): Array<number> => {
  const length = end - start + 1;
  /*
  	Create an array of certain length and set the elements within it from
    start value to end value.
  */
  return Array.from({ length }, (_, idx) => idx + start);
};

const DOTS = 'Dots';

interface IUsePaginationProps {
  totalCount: number;
  pageSize: number;
  siblingCount: number;
  currentPage: number;
}

const usePagination = ({ totalCount, pageSize, siblingCount = 1, currentPage }: IUsePaginationProps) => {
  const paginationRange = React.useMemo(() => {
    const totalPageCount = Math.ceil(totalCount / pageSize);

    // Pages count is determined as siblingCount + firstPage + lastPage + currentPage + 2*DOTS
    const totalPageNumbers = siblingCount + 5;

    /*
      Case 1:
      If the number of pages is less than the page numbers we want to show in our
      paginationComponent, we return the range [1..totalPageCount]
    */
    if (totalPageNumbers >= totalPageCount) {
      return range(1, totalPageCount);
    }

    /*
    	Calculate left and right sibling index and make sure they are within range 1 and totalPageCount
    */
    const leftSiblingIndex = Math.max(currentPage - siblingCount, 1);
    const rightSiblingIndex = Math.min(currentPage + siblingCount, totalPageCount);

    /*
      We do not show dots just when there is just one page number to be inserted between the extremes of sibling and the page limits i.e 1 and totalPageCount. Hence we are using leftSiblingIndex > 2 and rightSiblingIndex < totalPageCount - 2
    */
    const shouldShowLeftDots = leftSiblingIndex > 2;
    const shouldShowRightDots = rightSiblingIndex < totalPageCount - 2;

    const firstPageIndex = 1;
    const lastPageIndex = totalPageCount;

    // 3 includes: the current page, the previous page and the next page
    // siblingCount represents items appearing next to the extremities but excludes the one appearing next to the visible set of dots
    const oneSideItemCount = 3 + 2 * siblingCount;

    /*
    	Case 2: No left dots to show, but rights dots to be shown
    */
    if (!shouldShowLeftDots && shouldShowRightDots) {
      const leftItemCount = oneSideItemCount;
      const leftRange = range(1, leftItemCount);

      return [...leftRange, DOTS, totalPageCount];
    }

    /*
    	Case 3: No right dots to show, but left dots to be shown
    */
    if (shouldShowLeftDots && !shouldShowRightDots) {
      const rightItemCount = oneSideItemCount;
      const rightRange = range(totalPageCount - rightItemCount + 1, totalPageCount);
      return [firstPageIndex, DOTS, ...rightRange];
    }

    /*
    	Case 4: Both left and right dots to be shown
    */
    if (shouldShowLeftDots && shouldShowRightDots) {
      const middleRange = range(leftSiblingIndex, rightSiblingIndex);
      return [firstPageIndex, DOTS, ...middleRange, DOTS, lastPageIndex];
    }
  }, [totalCount, pageSize, siblingCount, currentPage]);

  return paginationRange;
};

interface IPaginationItemProps {
  disabled?: boolean;
  onClick?: React.MouseEventHandler;
  className?: string;
}

const PaginationItem = (props: React.PropsWithChildren<IPaginationItemProps>) => {
  if (props.disabled) {
    return <li className={`pagination-item ${props.className || ''} disabled`}>{props.children}</li>;
  }

  return (
    <li>
      <a href='#' className={`pagination-item ${props.className || ''}`} onClick={props.onClick}>
        {props.children}
      </a>
    </li>
  );
};

interface IPaginationProps {
  onPageChange: Function;
  totalCount: number;
  siblingCount?: number;
  currentPage: number;
  pageSize: number;
  className?: string;
}
export const Pagination = ({
  onPageChange,
  totalCount,
  siblingCount = 1,
  currentPage,
  pageSize,
  className
}: IPaginationProps) => {
  const paginationRange = usePagination({
    currentPage,
    totalCount,
    siblingCount,
    pageSize
  });

  // If there are less than 2 times in pagination range we shall not render the component
  if (currentPage === 0 || paginationRange.length < 2) {
    return null;
  }

  const onNext = () => {
    onPageChange(currentPage + 1);
  };

  const onPrevious = () => {
    onPageChange(currentPage - 1);
  };

  let lastPage = paginationRange[paginationRange.length - 1];
  return (
    <ul className={`pagination-container ${className}`}>
      {/* Left navigation arrow */}
      <PaginationItem disabled={currentPage === 1} onClick={onPrevious}>
        <span className='visually-hidden'>Previous page</span>
        <div className='arrow left' aria-hidden='true'>
          &nbsp;
        </div>
      </PaginationItem>
      {paginationRange.map((pageNumber, index) => {
        // If the pageItem is a DOT, render the DOTS unicode character
        const key = `${pageNumber}-${index}`;
        if (pageNumber === DOTS) {
          return (
            <PaginationItem key={key} className='dots'>
              &#8230;
            </PaginationItem>
          );
        }

        // Render our Page Pills
        return (
          <PaginationItem
            key={key}
            className={pageNumber === currentPage ? 'selected' : ''}
            onClick={() => onPageChange(pageNumber)}
          >
            {pageNumber}
          </PaginationItem>
        );
      })}
      {/*  Right Navigation arrow */}
      <PaginationItem disabled={currentPage === lastPage} onClick={onNext}>
        <span className='visually-hidden'>Next page</span>
        <div className='arrow right' aria-hidden='true'>
          &nbsp;
        </div>
      </PaginationItem>
    </ul>
  );
};
